import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import PendingApprovalsSection from '../components/common/PendingApprovalsSection';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Alert,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  DragIndicator,
  ViewModule as ViewModuleIcon,
  AttachMoney,
  Info,
  Person,
  CreditCard,
  DirectionsCar,
  Restaurant,
  Business,
  Home,
  ShoppingCart,
  LocalShipping,
  Build,
  AccountBalance,
  Apartment,
  LocalHospital,
  School,
  Park,
  Agriculture,
  WaterDrop,
  LocalBar,
  Storefront,
  Engineering,
} from '@mui/icons-material';
import { useModules, usePermission } from '../hooks';
import { moduleService } from '../services/module.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { colors } from '../theme/theme';

export const ModulesPage = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);

  const handleApprovalComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['modules'] });
  };

  // Icon options for modules
  const iconOptions = [
    { name: 'Money', icon: AttachMoney, value: 'AttachMoney' },
    { name: 'Card', icon: CreditCard, value: 'CreditCard' },
    { name: 'Car', icon: DirectionsCar, value: 'DirectionsCar' },
    { name: 'Restaurant', icon: Restaurant, value: 'Restaurant' },
    { name: 'Business', icon: Business, value: 'Business' },
    { name: 'House', icon: Home, value: 'Home' },
    { name: 'Shopping', icon: ShoppingCart, value: 'ShoppingCart' },
    { name: 'Shipping', icon: LocalShipping, value: 'LocalShipping' },
    { name: 'Tools', icon: Build, value: 'Build' },
    { name: 'Bank', icon: AccountBalance, value: 'AccountBalance' },
    { name: 'Building', icon: Apartment, value: 'Apartment' },
    { name: 'Hospital', icon: LocalHospital, value: 'LocalHospital' },
    { name: 'School', icon: School, value: 'School' },
    { name: 'Park', icon: Park, value: 'Park' },
    { name: 'Agriculture', icon: Agriculture, value: 'Agriculture' },
    { name: 'Water', icon: WaterDrop, value: 'WaterDrop' },
    { name: 'Bar', icon: LocalBar, value: 'LocalBar' },
    { name: 'Store', icon: Storefront, value: 'Storefront' },
    { name: 'Engineering', icon: Engineering, value: 'Engineering' },
  ];

  const { modules, isLoading, error } = useModules({
    page: 1,
    per_page: 100,
    search,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => moduleService.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      handleCloseDeleteDialog();
    },
  });

  const handleOpenDeleteDialog = (module: any) => {
    setModuleToDelete(module);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setModuleToDelete(null);
  };

  const handleDelete = () => {
    if (moduleToDelete) {
      deleteMutation.mutate(moduleToDelete.id);
    }
  };

  const handleViewModule = (moduleId: number) => {
    navigate(`/modules/${moduleId}`);
  };

  const getTypeColor = (type: number) => {
    return type === 1 ? 'warning' : 'primary';
  };

  const getBillableColor = (isBillable: boolean) => {
    return isBillable ? 'success' : 'info';
  };

  const getModuleIcon = (moduleType: any) => {
    // Return icon based on module type or use default
    if (moduleType?.icon) return moduleType.icon;
    return '📋';
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Modules"
          subtitle="Manage county service modules and configurations"
          action={
            can('modules.create') ? <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/modules/create')}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Create Module
            </Button> : undefined
          }
        />

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Modules" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            {/* Search Bar */}
            <Box mb={4}>
              <TextField
                fullWidth
                placeholder="Search modules by name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: 'text.secondary', mr: 1.5, fontSize: '1.25rem' }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: colors.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary.main,
                    },
                  },
                }}
              />
            </Box>

            {/* Info Alert */}
            {modules?.data && modules.data.length > 0 && (
              <Alert
                severity="info"
                icon={<Info />}
                sx={{
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(13, 202, 240, 0.08)',
                  border: '1px solid rgba(13, 202, 240, 0.2)',
                  '& .MuiAlert-icon': {
                    color: '#0dcaf0',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Tip: Drag and drop the cards to reorder modules as they appear in the mobile app.
                </Typography>
              </Alert>
            )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load modules. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: '#e2e8f0',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 3 }} />
                      <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
                      <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 2 }} />
                    </Box>
                    <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2.5 }} />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Skeleton variant="rectangular" height={42} sx={{ flex: 1, borderRadius: 2 }} />
                      <Skeleton variant="rectangular" width={42} height={42} sx={{ borderRadius: 2 }} />
                      <Skeleton variant="rectangular" width={42} height={42} sx={{ borderRadius: 2 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : !modules?.data || modules.data.length === 0 ? (
          <Card
            sx={{
              textAlign: 'center',
              py: 8,
              px: 4,
              borderRadius: 4,
              border: '2px dashed',
              borderColor: `${colors.primary.main}20`,
              bgcolor: colors.primary[50],
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: colors.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ViewModuleIcon sx={{ fontSize: 50, color: colors.primary.main }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
              No Modules Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              Get started by creating your first module to manage county services.
            </Typography>
            {can('modules.create') && <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate('/modules/create')}
              sx={{
                background: colors.gradients.primary,
                px: 4,
                py: 1.5,
              }}
            >
              Create Module
            </Button>}
          </Card>
        ) : (
          <Grid container spacing={3}>
            {modules.data.map((module: any) => (
              <Grid key={module.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: '#e2e8f0',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${colors.primary.main}25`,
                      borderColor: module.is_billable ? colors.primary.main : '#0dcaf0',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Icon and Status */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          background: module.background_color || (module.is_billable
                            ? colors.gradients.primary
                            : 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2rem',
                          boxShadow: module.is_billable
                            ? colors.shadows.button
                            : '0 4px 12px rgba(13, 202, 240, 0.25)',
                          overflow: 'hidden',
                        }}
                      >
                        {(() => {
                          // If icon is a Material-UI icon name, render the icon component
                          const iconOption = iconOptions.find(opt => opt.value === module.icon);
                          if (iconOption) {
                            const IconComp = iconOption.icon;
                            return <IconComp sx={{ fontSize: 36, color: 'white' }} />;
                          }
                          // If icon is an image URL
                          if (module.icon && (module.icon.endsWith('.jpg') || module.icon.endsWith('.png') || module.icon.endsWith('.svg') || module.icon.includes('/'))) {
                            return (
                              <img
                                src={module.icon.startsWith('http') ? module.icon : `/storage/${module.icon}`}
                                alt={module.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = '📋';
                                }}
                              />
                            );
                          }
                          // Fallback to emoji or default icon
                          return module.icon || <AttachMoney sx={{ fontSize: 36, color: 'white' }} />;
                        })()}
                      </Box>
                      <Chip
                        label={module.status === 'active' ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: module.status === 'active' ? '#dcfce7' : '#f3f4f6',
                          color: module.status === 'active' ? '#15803d' : '#6b7280',
                          border: 'none',
                        }}
                      />
                    </Box>

                    {/* Module Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: '1.125rem',
                        color: '#0f172a',
                        lineHeight: 1.3,
                      }}
                    >
                      {module.name}
                    </Typography>

                    {/* Tags */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={module.module_type?.name || 'Module'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: '#eff6ff',
                          color: '#1e40af',
                          border: 'none',
                        }}
                      />
                      <Chip
                        label={module.type === 1 ? 'Unstructured' : 'Structured'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: module.type === 1 ? '#fef3c7' : '#ddd6fe',
                          color: module.type === 1 ? '#92400e' : '#5b21b6',
                          border: 'none',
                        }}
                      />
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.6,
                        mb: 2.5,
                        minHeight: 42,
                        fontSize: '0.875rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {module.description || 'No description available'}
                    </Typography>

                    {/* Stats - Without Gray Background */}
                    {module.is_billable && (module.services_count > 0 || module.charges_count > 0) && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 3,
                          mb: 2.5,
                          pb: 2.5,
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Services
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary.main, fontSize: '1.5rem' }}>
                            {module.services_count || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Charges
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0dcaf0', fontSize: '1.5rem' }}>
                            {module.charges_count || 0}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => handleViewModule(module.id)}
                        sx={{
                          py: 1.25,
                          background: module.is_billable
                            ? colors.gradients.primary
                            : 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: module.is_billable
                              ? colors.shadows.buttonHover
                              : '0 4px 12px rgba(13, 202, 240, 0.4)',
                          },
                        }}
                      >
                        Open Module
                      </Button>
                      {can('modules.edit') && <IconButton
                        onClick={() => navigate(`/modules/${module.id}/edit`)}
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          bgcolor: 'white',
                          color: '#64748b',
                          '&:hover': {
                            bgcolor: '#f8fafc',
                            color: colors.primary.main,
                            borderColor: '#cbd5e1',
                          },
                        }}
                      >
                        <Edit sx={{ fontSize: '1rem' }} />
                      </IconButton>}
                      {can('modules.delete') && <IconButton
                        onClick={() => handleOpenDeleteDialog(module)}
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          bgcolor: 'white',
                          color: '#64748b',
                          '&:hover': {
                            bgcolor: '#fef2f2',
                            color: '#dc2626',
                            borderColor: '#fecaca',
                          },
                        }}
                      >
                        <Delete sx={{ fontSize: '1rem' }} />
                      </IconButton>}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 12px 48px rgba(231, 29, 54, 0.2)',
            },
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(231, 29, 54, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Delete sx={{ fontSize: 32, color: '#e71d36' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Delete Module
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. This module will be permanently removed.
            </Typography>
          </DialogTitle>
          <DialogActions sx={{ p: 3, justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseDeleteDialog} sx={{ px: 3 }} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              startIcon={deleteMutation.isPending ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Delete />}
              sx={{ px: 4 }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Module'}
            </Button>
          </DialogActions>
        </Dialog>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="module"
            title="Pending Module Approvals"
            onApprovalComplete={handleApprovalComplete}
          />
        )}
      </Box>
    </AdminLayout>
  );
};
